import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setConnectionAgentAttendeeCollection,
  setConnectionAgentFormat,
  setConnectionAgentSessionsCollection,
  setConnectionAgentTitle,
  toggleConnectionAgentFormat,
  toggleConnectionAgentPairing,
} from '../../../../../../redux/slices/agents/agentSlice';
import { useGetAllEnginesQuery, useListCollectionsQuery } from '../../../../../../services/api';
import { selectSelectedEvent } from '../../../../../../redux/slices/events/eventsSlice';
import { selectIsEventsPlatform } from '../../../../../../redux/slices/platform/platformSlice';
import { COLLECTION_KINDS } from '../../../../../../constants/collections-v2/collectionKinds';
import {
  connectionSlotKey,
  engineFormatOf,
} from '../../../../../../constants/agents/connectionAgent';

// Formats that pick from the event's programme ('sessions' is a draft saved before the slot's name).
export const PROGRAMME_FORMATS = ['one_to_many', 'session_recs', 'sessions'];

/**
 * The Event Database's collections of one kind for the agent's event (every event's when none is
 * picked). The Connection agent's attendees and programme are picked from these (D38, D39).
 * An agent being edited uses its own event's, so a copy made for another event lists that event's.
 */
export const useEventCollections = collectionType => {
  const selectedEvent = useSelector(selectSelectedEvent);
  const editing = useSelector(state => state.agent.connectionAgent);
  // Only within an event: without one, every event's collections show as before. An event picked in
  // the setup itself (no event open) lists that event's.
  const agentEventId =
    (selectedEvent && editing?.existing) || !selectedEvent ? editing?.eventId || null : null;
  const eventId = agentEventId || selectedEvent?.id || selectedEvent?._id;
  const { data, isLoading } = useListCollectionsQuery({
    collectionType,
    ...(eventId ? { eventId } : {}),
  });
  const collections = useMemo(
    () => (Array.isArray(data?.collections) ? data.collections : []),
    [data]
  );
  return {
    collections,
    isLoading,
    eventName: agentEventId ? editing.eventName || selectedEvent?.name : selectedEvent?.name,
  };
};

/**
 * The event the agent is for: the one it was saved with, else the event open in the Events platform.
 * A live agent needs one (QA round 3, E1), so without it the Define step asks for it.
 */
export const useConnectionAgentEvent = () => {
  const selectedEvent = useSelector(selectSelectedEvent);
  const isEventsPlatform = useSelector(selectIsEventsPlatform);
  const {
    eventId: savedId,
    eventName: savedName,
    existing,
  } = useSelector(state => state.agent.connectionAgent);
  const openEvent = isEventsPlatform ? selectedEvent : null;
  const eventId = savedId || openEvent?.id || openEvent?._id || null;
  return {
    eventId,
    eventName: savedId
      ? savedName || (savedId === openEvent?.id ? openEvent?.name : null)
      : openEvent?.name || null,
    // Picked in the Define step: a new setup with no event open, or an agent saved without one.
    canPick: existing?.agentId ? !savedId : !openEvent,
  };
};

/**
 * The Connection agent's Define step: its name, the formats it runs (one matching engine each),
 * and what it matches, both picked from the Event Database: the attendee collection (delegates and
 * sponsors) and, when a format needs sessions, the event collection with the programme.
 */
const useConnectionAgentDefineHandler = () => {
  const dispatch = useDispatch();
  // `existing`: editing an agent after it's made (who is in it now); null for a new setup.
  const { title, formats, sessionsCollectionId, attendeeCollectionId, existing } = useSelector(
    state => state.agent.connectionAgent
  );

  // ---- engines: one per slot (a format, and for one to one each pairing), picked in a side panel ----
  const [engineDrawerKey, setEngineDrawerKey] = useState(null);
  const [localEngine, setLocalEngine] = useState(null);
  const { data: enginesResponse, isLoading: isEnginesLoading } = useGetAllEnginesQuery();
  const matchingEngines = useMemo(
    () =>
      (enginesResponse?.data || []).filter(engine => engine?.engineData?.engineType === 'matching'),
    [enginesResponse]
  );

  const enginesForFormat = slot =>
    matchingEngines.filter(
      engine =>
        engine.engineData?.format === engineFormatOf(slot?.format) &&
        (slot?.format !== 'one_to_one' || engine.engineData?.pairing === slot?.pairing)
    );

  const drawerSlot = formats.find(item => connectionSlotKey(item) === engineDrawerKey) || null;

  const openEngineDrawer = key => {
    const slot = formats.find(item => connectionSlotKey(item) === key);
    setLocalEngine(matchingEngines.find(engine => engine._id === slot?.engineId) || null);
    setEngineDrawerKey(key);
  };
  const closeEngineDrawer = () => setEngineDrawerKey(null);
  // A new engine runs on its live version until a version is picked.
  const handleSelectEngine = engine => {
    if (!engine?._id || !engineDrawerKey) return;
    dispatch(
      setConnectionAgentFormat({
        key: engineDrawerKey,
        engineId: engine._id,
        engineTitle: engine.title,
        engineVersionId: null,
        engineVersion: null,
      })
    );
    closeEngineDrawer();
  };
  const handleRemoveEngine = key =>
    dispatch(
      setConnectionAgentFormat({
        key,
        engineId: null,
        engineTitle: null,
        engineVersionId: null,
        engineVersion: null,
      })
    );
  const handleEngineVersionChange = (key, version) =>
    dispatch(
      setConnectionAgentFormat({
        key,
        engineVersionId: version?._id || null,
        engineVersion: version?.title || null,
      })
    );

  const handleToggleFormat = format => dispatch(toggleConnectionAgentFormat(format));
  // One to one can run both pairings at once (D2), each on its own engine.
  const handleTogglePairing = pairing => dispatch(toggleConnectionAgentPairing(pairing));

  // ---- the programme: an event collection ----
  const [isCollectionDrawerOpen, setIsCollectionDrawerOpen] = useState(false);
  const [localCollection, setLocalCollection] = useState(null);
  const { collections: collectionsData, isLoading: isCollectionsLoading } = useEventCollections(
    COLLECTION_KINDS.sessions
  );
  const selectedCollection =
    collectionsData.find(collection => collection._id === sessionsCollectionId) || null;
  const needsProgramme = formats.some(item => PROGRAMME_FORMATS.includes(item.format));

  const openCollectionDrawer = () => {
    setLocalCollection(selectedCollection);
    setIsCollectionDrawerOpen(true);
  };
  const closeCollectionDrawer = () => setIsCollectionDrawerOpen(false);
  const handleSelectCollection = collection => {
    dispatch(setConnectionAgentSessionsCollection(collection));
    closeCollectionDrawer();
  };
  const handleRemoveCollection = () => dispatch(setConnectionAgentSessionsCollection(null));

  // ---- attendees: an attendee collection ----
  const [isAttendeeDrawerOpen, setIsAttendeeDrawerOpen] = useState(false);
  const [localAttendeeCollection, setLocalAttendeeCollection] = useState(null);
  const {
    collections: attendeeCollections,
    isLoading: isAttendeeCollectionsLoading,
    eventName,
  } = useEventCollections(COLLECTION_KINDS.attendees);
  const selectedAttendeeCollection =
    attendeeCollections.find(collection => collection._id === attendeeCollectionId) || null;
  const attendeeCounts = selectedAttendeeCollection?.collectionData?.recordCounts || {};

  const openAttendeeDrawer = () => {
    setLocalAttendeeCollection(selectedAttendeeCollection);
    setIsAttendeeDrawerOpen(true);
  };
  const closeAttendeeDrawer = () => setIsAttendeeDrawerOpen(false);
  const handleSelectAttendeeCollection = collection => {
    dispatch(setConnectionAgentAttendeeCollection(collection));
    closeAttendeeDrawer();
  };
  const handleRemoveAttendeeCollection = () => dispatch(setConnectionAgentAttendeeCollection(null));

  // "Manage in Event Database" opens it in a new tab, so the agent being set up stays as it is.
  const openEventDatabase = () => window.open('/collections/dashboard', '_blank')?.focus();
  const openCollectionPage = collection =>
    window.open(`/collections/create?_id=${collection?._id}`, '_blank')?.focus();

  return {
    title,
    handleTitleChange: event => dispatch(setConnectionAgentTitle(event.target.value)),
    formats,
    handleToggleFormat,
    handleTogglePairing,
    eventName,
    openEventDatabase,
    openCollectionPage,
    // engines
    isEnginesLoading,
    enginesForFormat,
    engineDrawerKey,
    drawerSlot,
    localEngine,
    setLocalEngine,
    openEngineDrawer,
    closeEngineDrawer,
    handleSelectEngine,
    handleRemoveEngine,
    handleEngineVersionChange,
    // programme
    needsProgramme,
    collectionsData,
    isCollectionsLoading,
    selectedCollection,
    isCollectionDrawerOpen,
    localCollection,
    setLocalCollection,
    openCollectionDrawer,
    closeCollectionDrawer,
    handleSelectCollection,
    handleRemoveCollection,
    // attendees
    existing,
    attendeeCollections,
    isAttendeeCollectionsLoading,
    selectedAttendeeCollection,
    attendeeCounts,
    isAttendeeDrawerOpen,
    localAttendeeCollection,
    setLocalAttendeeCollection,
    openAttendeeDrawer,
    closeAttendeeDrawer,
    handleSelectAttendeeCollection,
    handleRemoveAttendeeCollection,
  };
};

export default useConnectionAgentDefineHandler;
